import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cartService } from '@/services/cart';
import type {
  AddToCartParams,
  AddToCartResult,
  RemoveFromCartParams,
  RemoveFromCartResult,
  ClearCartResult,
  BulkAllocateParams,
  BulkAllocateResult,
  WorkflowType,
} from '@/services/cart';
import { cartQueryKeys } from '@/hooks/queries/cart/useCartQueries';
import { useNotifications } from '@/stores/useNotificationStore';
import { logger } from '@equip/utils/logger';

const log = logger.child({ component: 'useCartMutations' });

const invalidateWorkflowCartQueries = async (
  queryClient: ReturnType<typeof useQueryClient>,
  customerId: string,
  workflowType?: WorkflowType,
) => {
  if (!workflowType) return;

  if (workflowType === 'maintenance-send' || workflowType === 'maintenance-receive') {
    await queryClient.invalidateQueries({
      queryKey: ['maintenance-cart', 'cart', workflowType, customerId],
    });
  }

  if (workflowType === 'transfer' || workflowType === 'shipment') {
    await queryClient.invalidateQueries({
      queryKey: ['transfers-shipping-cart', 'cart', workflowType, customerId],
    });
  }
};

/**
 * Hook to add assets to the workflow cart.
 * Creates cart as needed and reserves assets in the database.
 */
export const useAddToCart = () => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation<AddToCartResult, Error, AddToCartParams>({
    mutationFn: (params) => cartService.addToCart(params),
    onSuccess: (result, variables) => {
      if (!result.success) {
        if (result.already_reserved && result.already_reserved.length > 0) {
          notifications.warning(
            'Some assets unavailable',
            `${result.already_reserved.length} asset(s) are already reserved by another user.`,
          );
        } else {
          notifications.error('Failed to add to cart', result.error ?? 'Unknown error');
        }
        return;
      }

      // Invalidate cart queries to refresh UI
      void queryClient.invalidateQueries({
        queryKey: cartQueryKeys.cart(variables.customerId),
      });

      // Also invalidate available assets since we just reserved some
      void queryClient.invalidateQueries({ queryKey: ['iem-available-assets'] });
      void queryClient.invalidateQueries({ queryKey: ['iem-additional-item-groups'] });
    },
    onError: (error) => {
      notifications.error('Failed to add to cart', error.message);
    },
  });
};

/**
 * Hook to remove assets from the cart.
 * Releases reservations for removed assets.
 */
export const useRemoveFromCart = () => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation<RemoveFromCartResult, Error, RemoveFromCartParams>({
    mutationFn: (params) => cartService.removeFromCart(params),
    onSuccess: async (result, variables) => {
      if (!result.success) {
        notifications.error('Failed to remove from cart', result.error ?? 'Unknown error');
        return;
      }

      // Invalidate cart queries to refresh UI
      await queryClient.invalidateQueries({
        queryKey: cartQueryKeys.cart(variables.customerId),
      });

      // Check if cart is empty and delete if so
      try {
        const { cart } = await cartService.getCart(variables.customerId);
        if (cart && cart.item_count === 0) {
          await cartService.deleteCart(cart.id);
          // Invalidate active carts list so it disappears from the table
          void queryClient.invalidateQueries({ queryKey: cartQueryKeys.activeAll() });
          // Invalidate cart again to ensure UI reflects "no cart"
          void queryClient.invalidateQueries({
            queryKey: cartQueryKeys.cart(variables.customerId),
          });
        }
      } catch (error) {
        log.debug('Failed to cleanup empty cart', { error: String(error) });
      }

      // Also invalidate available assets since we just released some
      void queryClient.invalidateQueries({ queryKey: ['iem-available-assets'] });
      void queryClient.invalidateQueries({ queryKey: ['iem-additional-item-groups'] });
    },
    onError: (error) => {
      notifications.error('Failed to remove from cart', error.message);
    },
  });
};

/**
 * Hook to clear an entire cart, releasing all reservations.
 */
export const useClearCart = () => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation<ClearCartResult, Error, { customerId: string; cartId?: string; workflowType?: WorkflowType }>({
    mutationFn: async ({ customerId, cartId, workflowType }) => {
      const result = await cartService.clearCart(customerId, workflowType);

      if (result.success && cartId) {
        try {
          await cartService.deleteCart(cartId);
        } catch (error) {
          log.debug('Failed to delete cart after clear', { error: String(error), cartId });
        }
      }

      return result;
    },
    onSuccess: async (result, variables) => {
      if (!result.success) {
        notifications.error('Failed to cancel cart', result.message ?? 'Unknown error');
        return;
      }

      notifications.success('Cart canceled', 'Reserved items were released.');

      await queryClient.invalidateQueries({ queryKey: cartQueryKeys.activeAll() });
      await queryClient.invalidateQueries({
        queryKey: cartQueryKeys.cart(variables.customerId),
      });
      await invalidateWorkflowCartQueries(
        queryClient,
        variables.customerId,
        variables.workflowType,
      );

      void queryClient.invalidateQueries({ queryKey: ['iem-available-assets'] });
      void queryClient.invalidateQueries({ queryKey: ['iem-additional-item-groups'] });
    },
    onError: (error) => {
      notifications.error('Failed to cancel cart', error.message);
    },
  });
};

export interface BulkClearCartsResult {
  succeeded: number;
  failed: number;
  errors: Array<{ cartId: string; message: string }>;
}

/**
 * Hook to cancel multiple carts at once.
 * Calls clearCart + deleteCart for each cart, then invalidates queries once at the end.
 */
export const useBulkClearCarts = () => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation<
    BulkClearCartsResult,
    Error,
    Array<{ customerId: string; cartId: string; workflowType?: WorkflowType }>
  >({
    mutationFn: async (carts) => {
      let succeeded = 0;
      let failed = 0;
      const errors: BulkClearCartsResult['errors'] = [];

      const results = await Promise.allSettled(
        carts.map(async ({ customerId, cartId, workflowType }) => {
          const result = await cartService.clearCart(customerId, workflowType);
          if (!result.success) {
            throw new Error(result.message ?? 'Failed to clear cart');
          }
          try {
            await cartService.deleteCart(cartId);
          } catch (error) {
            log.debug('Failed to delete cart after bulk clear', { error: String(error), cartId });
          }
          return { cartId };
        }),
      );

      for (let i = 0; i < results.length; i++) {
        const r = results[i]!;
        if (r.status === 'fulfilled') {
          succeeded++;
        } else {
          failed++;
          errors.push({ cartId: carts[i]!.cartId, message: (r as PromiseRejectedResult).reason?.message ?? 'Unknown error' });
        }
      }

      return { succeeded, failed, errors };
    },
    onSuccess: async (result) => {
      if (result.failed > 0 && result.succeeded > 0) {
        notifications.warning(
          'Partial cancellation',
          `${result.succeeded} cart(s) canceled. ${result.failed} could not be canceled; they may already be empty or no longer active.`,
        );
      } else if (result.failed > 0) {
        notifications.error(
          'Unable to cancel selected carts',
          `${result.failed} selected cart(s) were unavailable to cancel. Refresh and try again.`,
        );
      } else {
        notifications.success(
          `${result.succeeded} cart(s) canceled`,
          'All reserved items were released.',
        );
      }

      await queryClient.invalidateQueries({ queryKey: cartQueryKeys.activeAll() });
      void queryClient.invalidateQueries({ queryKey: ['iem-available-assets'] });
      void queryClient.invalidateQueries({ queryKey: ['iem-additional-item-groups'] });
    },
    onError: (error) => {
      notifications.error('Failed to cancel carts', error.message);
    },
  });
};

/**
 * Hook to bulk allocate assets to the cart by LINs.
 * Finds available assets matching requested LINs and creates reservations atomically.
 */
export const useBulkAllocateToCart = () => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation<BulkAllocateResult, Error, BulkAllocateParams>({
    mutationFn: (params) => cartService.bulkAllocateToCart(params),
    onSuccess: (result, variables) => {
      if (!result.success) {
        notifications.error('Failed to allocate assets', result.error ?? 'Unknown error');
        return;
      }

      // Report partial allocations (skip when called silently, e.g. auto-populate)
      if (!variables.silent) {
        const items = result.items ?? [];
        const shortfalls = items.filter((item) => item.allocated < item.requested);
        if (shortfalls.length > 0) {
          const summary = shortfalls
            .map((item) => `${item.item_name}: ${item.allocated}/${item.requested}`)
            .join(', ');
          notifications.warning('Limited inventory', `Some items partially allocated: ${summary}`);
        }
      }

      // Invalidate cart queries to refresh UI
      void queryClient.invalidateQueries({
        queryKey: cartQueryKeys.cart(variables.customerId),
      });

      // Also invalidate available assets since we just reserved some
      void queryClient.invalidateQueries({ queryKey: ['iem-available-assets'] });
      void queryClient.invalidateQueries({ queryKey: ['iem-additional-item-groups'] });
    },
    onError: (error) => {
      notifications.error('Failed to allocate assets', error.message);
    },
  });
};

/**
 * Hook to upsert a size allocation cart placeholder for a parent catalog item.
 * Placeholders represent unsized items awaiting size selection and asset allocation.
 * They do not reserve specific assets or impact stock availability.
 */
export const useUpsertSizeAllocationCart = () => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation<
    {
      success: boolean;
      cart_id: string | null;
      placeholder_id: string | null;
      quantity: number;
      error?: string;
    },
    Error,
    { customerId: string; parentCatalogId: string; quantity: number }
  >({
    mutationFn: (params) => cartService.upsertSizeAllocationCart(params),
    onSuccess: (result, variables) => {
      if (!result.success) {
        notifications.error(
          'Failed to save placeholder',
          result.error ?? 'Unknown error while updating size allocation.',
        );
        return;
      }

      // Refresh the workflow cart so any downstream consumers can see placeholder changes.
      void queryClient.invalidateQueries({
        queryKey: cartQueryKeys.cart(variables.customerId),
      });
    },
    onError: (error) => {
      notifications.error('Failed to save placeholder', error.message);
    },
  });
};
